import React from 'react';
import styled, { keyframes, css } from 'styled-components';
import { 
  FiUser, FiMail, FiPhone, FiMapPin, FiCalendar, 
  FiDollarSign, FiHome, FiBriefcase, FiHeart,
  FiUsers, FiPieChart, FiFlag, FiClock, FiAward, FiEdit2
} from 'react-icons/fi';
import { FaDog, FaCat, FaPaw } from 'react-icons/fa';
import { IoMdClose } from 'react-icons/io';

// Animação premium melhorada
const modalEntrance = keyframes`
  0% { opacity: 0; transform: translateY(30px) scale(0.98); }
  100% { opacity: 1; transform: translateY(0) scale(1); }
`;

const backdropFade = keyframes`
  from { opacity: 0; backdrop-filter: blur(0); }
  to { opacity: 1; backdrop-filter: blur(8px); }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(15, 23, 42, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: ${backdropFade} 0.4s ease-out forwards;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 920px;
  max-height: 90vh;
  overflow: hidden;
  position: relative;
  box-shadow: 0 20px 50px -10px rgba(15, 23, 42, 0.25);
  animation: ${modalEntrance} 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
  display: flex;
  flex-direction: column;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.75rem 2.5rem;
  background: #1e293b;
  color: white;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
  }
  
  h3 {
    font-size: 1.5rem;
    margin: 0;
    font-weight: 600;
    letter-spacing: -0.25px;
    color: #f8fafc;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    
    span {
      opacity: 0.8;
      font-weight: 500;
      font-size: 1rem;
      margin-left: 0.5rem;
    }
  }
`;

const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #f8fafc;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(255, 255, 255, 0.1);
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover {
    background: rgba(255, 255, 255, 0.15);
    transform: rotate(90deg);
    
    &::before {
      opacity: 1;
    }
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const ModalBody = styled.div`
  padding: 2.5rem;
  overflow-y: auto;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.75rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    padding: 1.75rem;
  }
`;

const InfoGroup = styled.div`
  background: ${({ highlight }) => highlight ? '#f8fafc' : '#fff'};
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: ${({ highlight }) => highlight 
    ? '0 4px 20px rgba(30, 41, 59, 0.08)' 
    : '0 2px 12px rgba(30, 41, 59, 0.04)'};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid ${({ highlight }) => highlight ? '#e2e8f0' : '#f1f5f9'};
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: ${({ highlight }) => highlight 
      ? '0 8px 25px rgba(30, 41, 59, 0.12)' 
      : '0 6px 20px rgba(30, 41, 59, 0.08)'};
    border-color: ${({ highlight }) => highlight ? '#cbd5e1' : '#e2e8f0'};
  }
`;

const InfoLabel = styled.div`
  font-size: 0.8rem;
  color: #64748b;
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  svg {
    color: #94a3b8;
    width: 16px;
    height: 16px;
  }
`;

const InfoValue = styled.div`
  font-size: 1.1rem;
  color: #1e293b;
  font-weight: 500;
  word-break: break-word;
  line-height: 1.5;
  
  ${({ empty }) => empty && css`
    color: #94a3b8;
    font-style: italic;
    font-weight: 400;
  `}
`;

const SectionTitle = styled.h4`
  grid-column: 1 / -1;
  font-size: 1.15rem;
  color: #334155;
  margin: 1.5rem 0 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-weight: 600;
  
  svg {
    color: #64748b;
  }
`;

const ObservationList = styled.ul`
  margin: 0;
  padding-left: 1.5rem;
  
  li {
    margin-bottom: 0.75rem;
    position: relative;
    line-height: 1.6;
    padding-left: 1.25rem;
    
    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0.65em;
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: #6366f1;
    }
  }
`;

const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  background: #f1f5f9;
  color: #475569;
  padding: 0.35rem 1rem;
  border-radius: 8px;
  font-size: 0.85rem;
  margin-right: 0.5rem;
  margin-bottom: 0.5rem;
  font-weight: 500;
  border: 1px solid #e2e8f0;
  transition: all 0.2s ease;
  
  &:hover {
    background: #e2e8f0;
    transform: translateY(-1px);
  }
`;

// Funções auxiliares
const formatDate = (dateString) => {
  if (!dateString) return 'Não informado';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  } catch (e) {
    return 'Não informado';
  }
};

const formatCurrency = (value) => {
  if (!value && value !== 0) return 'Não informado';
  return new Intl.NumberFormat('pt-PT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

const getPetIcon = (type) => {
  if (!type) return <FaPaw />;
  if (type.toLowerCase().includes('cão') || type.toLowerCase().includes('cachorro')) return <FaDog />;
  if (type.toLowerCase().includes('gato')) return <FaCat />;
  return <FaPaw />;
};

const translateVisaType = (visaType) => {
  if (!visaType) return 'Não informado';
  
  const translations = {
    'temporary_stay': 'Estada Temporária',
    'prt': 'Procura de Trabalho',
    'residence': 'Residência',
    'cplp': 'Visto CPLP',
    'student': 'Estudante',
    'family_reunification': 'Reagrupamento Familiar',
    'digital_nomad': 'Nômade Digital',
    'retirement': 'Reforma/Aposentadoria',
    'golden_visa': 'Golden Visa',
    'd7': 'Visto D7',
    'd2': 'Visto D2 (Empreendedor)',
    'security': 'Segurança',
    'quality-of-life': 'Qualidade de vida',
    'studies': 'Estudos',
    'work': 'Trabalho',
    'other': 'Outro'
  };

  // Remove underscores/hifens e converte para lowercase para melhor correspondência
  const normalizedType = visaType.toLowerCase().replace(/[_-]/g, ' ');
  
  // Verifica se temos uma tradução direta
  for (const [key, value] of Object.entries(translations)) {
    if (normalizedType.includes(key.toLowerCase().replace(/[_-]/g, ' '))) {
      return value;
    }
  }

  // Se não encontrar, retorna o valor original formatado
  return visaType
    .split(/[_-]/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const ModalLeadDetails = ({ lead, onClose }) => {
  if (!lead) return null;

  // Função para tratar valores nulos/undefined/vazios
  const getDisplayValue = (value, defaultValue = 'Não informado') => {
    if (value === null || value === undefined || value === '') return defaultValue;
    return value;
  };

  // Função específica para número de filhos
  const displayNumberOfChildren = () => {
    if (lead.numberOfChildren === null || lead.numberOfChildren === undefined) return 'Não informado';
    if (lead.numberOfChildren === 0) return '0';
    return lead.numberOfChildren;
  };

  // Função para formatar motivos de mudança
  const formatMovingReason = (reason) => {
    if (!reason) return 'Não informado';
    if (Array.isArray(reason)) {
      return reason.map(r => translateVisaType(r)).join(', ');
    }
    return translateVisaType(reason);
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <h3>
            <FiUser /> Detalhes do Lead 
            <span>{lead.name ? `- ${lead.name}` : ''}</span>
          </h3>
          <CloseButton onClick={onClose} aria-label="Fechar modal">
            <IoMdClose />
          </CloseButton>
        </ModalHeader>
        
        <ModalBody>
          {/* Seção: Informações Básicas */}
          <SectionTitle>
            <FiUser /> Informações Pessoais
          </SectionTitle>
          
          <InfoGroup highlight>
            <InfoLabel><FiUser /> Nome Completo</InfoLabel>
            <InfoValue empty={!lead.name}>{getDisplayValue(lead.name)}</InfoValue>
          </InfoGroup>
          
          <InfoGroup>
            <InfoLabel><FiMail /> Email</InfoLabel>
            <InfoValue empty={!lead.email}>{getDisplayValue(lead.email)}</InfoValue>
          </InfoGroup>
          
          <InfoGroup>
            <InfoLabel><FiPhone /> Telefone</InfoLabel>
            <InfoValue empty={!lead.phone}>{getDisplayValue(lead.phone)}</InfoValue>
          </InfoGroup>
          
          <InfoGroup>
            <InfoLabel><FiFlag /> Nacionalidade</InfoLabel>
            <InfoValue empty={!lead.nationality}>{getDisplayValue(lead.nationality, 'Não informada')}</InfoValue>
          </InfoGroup>
          
          <InfoGroup>
            <InfoLabel><FiFlag /> Tipo de Visto</InfoLabel>
            <InfoValue empty={!lead.visaType}>{translateVisaType(lead.visaType)}</InfoValue>
          </InfoGroup>
          
          <InfoGroup>
            <InfoLabel><FiHeart /> Motivo da Mudança</InfoLabel>
            <InfoValue empty={!lead.movingReason}>{formatMovingReason(lead.movingReason)}</InfoValue>
          </InfoGroup>
          
          {/* Seção: Localização */}
          <SectionTitle>
            <FiMapPin /> Localização
          </SectionTitle>
          
          <InfoGroup>
            <InfoLabel><FiMapPin /> Região de Interesse</InfoLabel>
            <InfoValue empty={!lead.region}>{getDisplayValue(lead.region, 'Não informada')}</InfoValue>
          </InfoGroup>
          
          <InfoGroup>
            <InfoLabel><FiMapPin /> Estado Atual</InfoLabel>
            <InfoValue empty={!lead.currentState}>{getDisplayValue(lead.currentState)}</InfoValue>
          </InfoGroup>
          
          <InfoGroup>
            <InfoLabel><FiMapPin /> Cidade de Destino</InfoLabel>
            <InfoValue empty={!lead.targetCity}>{getDisplayValue(lead.targetCity, 'Não informada')}</InfoValue>
          </InfoGroup>
          
          {/* Seção: Profissional */}
          <SectionTitle>
            <FiBriefcase /> Situação Profissional
          </SectionTitle>
          
          <InfoGroup>
            <InfoLabel><FiBriefcase /> Profissão</InfoLabel>
            <InfoValue empty={!lead.profession}>{getDisplayValue(lead.profession, 'Não informada')}</InfoValue>
          </InfoGroup>
          
          <InfoGroup>
            <InfoLabel><FiAward /> Plano Financeiro</InfoLabel>
            <InfoValue empty={!lead.financialPlan}>{getDisplayValue(lead.financialPlan)}</InfoValue>
          </InfoGroup>
          
          {/* Seção: Imóvel */}
          <SectionTitle>
            <FiHome /> Interesse Imobiliário
          </SectionTitle>
          
          <InfoGroup>
            <InfoLabel><FiHome /> Tipo de Imóvel</InfoLabel>
            <InfoValue empty={!lead.propertyType}>{getDisplayValue(lead.propertyType)}</InfoValue>
          </InfoGroup>
          
          <InfoGroup>
            <InfoLabel><FiDollarSign /> Valor Máx. Compra</InfoLabel>
            <InfoValue empty={!lead.maxPurchaseValue && lead.maxPurchaseValue !== 0}>
              {lead.maxPurchaseValue || lead.maxPurchaseValue === 0 ? formatCurrency(lead.maxPurchaseValue) : 'Não informado'}
            </InfoValue>
          </InfoGroup>
          
          <InfoGroup>
            <InfoLabel><FiDollarSign /> Valor Máx. Aluguel</InfoLabel>
            <InfoValue empty={!lead.maxRentValue && lead.maxRentValue !== 0}>
              {lead.maxRentValue || lead.maxRentValue === 0 ? formatCurrency(lead.maxRentValue) : 'Não informado'}
            </InfoValue>
          </InfoGroup>
          
          {/* Seção: Família */}
          <SectionTitle>
            <FiUsers /> Situação Familiar
          </SectionTitle>
          
          <InfoGroup>
            <InfoLabel><FiHeart /> Nome do Cônjuge</InfoLabel>
            <InfoValue empty={!lead.spouseName}>{getDisplayValue(lead.spouseName)}</InfoValue>
          </InfoGroup>
          
          <InfoGroup>
            <InfoLabel><FiBriefcase /> Profissão do Cônjuge</InfoLabel>
            <InfoValue empty={!lead.spouseProfession}>{getDisplayValue(lead.spouseProfession, 'Não informada')}</InfoValue>
          </InfoGroup>
          
          <InfoGroup>
            <InfoLabel><FiUsers /> Número de Filhos</InfoLabel>
            <InfoValue empty={lead.numberOfChildren === null || lead.numberOfChildren === undefined}>
              {displayNumberOfChildren()}
            </InfoValue>
          </InfoGroup>
          
          <InfoGroup>
            <InfoLabel>
              {getPetIcon(lead.petType)} 
              Tipo de Pet
            </InfoLabel>
            <InfoValue empty={!lead.petType}>
              {lead.petType ? `${lead.petType}${lead.petSize ? ` (${lead.petSize})` : ''}` : 'Não informado'}
            </InfoValue>
          </InfoGroup>
          
          {/* Seção: Tempo */}
          <SectionTitle>
            <FiClock /> Cronograma
          </SectionTitle>
          
          <InfoGroup>
            <InfoLabel><FiCalendar /> Data de Realocação</InfoLabel>
            <InfoValue empty={!lead.relocationDate}>{formatDate(lead.relocationDate)}</InfoValue>
          </InfoGroup>
          
          <InfoGroup>
            <InfoLabel><FiCalendar /> Data de Chegada</InfoLabel>
            <InfoValue empty={!lead.arrivalDate}>{formatDate(lead.arrivalDate)}</InfoValue>
          </InfoGroup>
          
          <InfoGroup>
            <InfoLabel><FiClock /> Tempo de Planejamento</InfoLabel>
            <InfoValue empty={!lead.planningTime}>{getDisplayValue(lead.planningTime)}</InfoValue>
          </InfoGroup>
          
          {/* Seção: Serviços */}
          <SectionTitle>
            <FiPieChart /> Serviços de Interesse
          </SectionTitle>
          
          <InfoGroup style={{ gridColumn: '1 / -1' }}>
            <InfoLabel><FiPieChart /> Tipos de Serviço</InfoLabel>
            <div>
              {lead.serviceType?.length > 0 ? (
                lead.serviceType.map((service, index) => (
                  <Tag key={index}>
                    {service}
                  </Tag>
                ))
              ) : (
                <InfoValue empty>Nenhum serviço selecionado</InfoValue>
              )}
            </div>
          </InfoGroup>
          
          {/* Seção: Observações */}
          <SectionTitle>
            <FiEdit2 /> Observações
          </SectionTitle>
          
          <InfoGroup style={{ gridColumn: '1 / -1' }}>
            {lead.observation ? (
              <ObservationList>
                {lead.observation.split('\n').filter(line => line.trim() !== '').map((line, i) => (
                  <li key={i}>
                    <InfoValue>{line}</InfoValue>
                  </li>
                ))}
              </ObservationList>
            ) : (
              <InfoValue empty>Nenhuma observação registrada</InfoValue>
            )}
          </InfoGroup>
        </ModalBody>
      </ModalContent>
    </ModalOverlay>
  );
};

export default ModalLeadDetails;